import { useState } from 'react'
import { clsx } from 'clsx'
import { SexualityPicker } from './SexualityPicker'
import { feedPrefsReady } from '../lib/feedPrefs'
import type { ContentKind, Tag } from '../types'
import { useGame } from '../store/game'

const KINDS: { value: ContentKind; label: string }[] = [
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Video' },
  { value: 'both', label: 'Both' },
]

/** Pre-round setup: sexuality first, then NSFW tags. */
export function TagPicker({
  tags,
  onStart,
  busy,
  ctaLabel = 'START',
  showKind = true,
}: {
  tags: Tag[]
  onStart: (tagIds: string[], kind: ContentKind) => void
  busy?: boolean
  ctaLabel?: string
  showKind?: boolean
}) {
  const remembered = useGame()
  const feedPrefs = useGame((s) => s.feedPrefs)
  const setGender = useGame((s) => s.setGender)
  const setOrientation = useGame((s) => s.setOrientation)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(remembered.selectedTagIds.filter((id) => tags.some((t) => t.id === id))),
  )
  const [kind, setKind] = useState<ContentKind>(showKind ? remembered.kind : 'image')

  const prefsReady = feedPrefsReady(feedPrefs)
  const canStart = prefsReady && selected.size > 0 && !busy

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })

  if (!tags.length) {
    return (
      <div className="cyber-panel mx-auto max-w-md p-6 text-center">
        <p className="goon-title text-2xl">NO NSFW TAGS YET</p>
        <p className="mt-2 text-sm text-muted">An admin needs to add tags to play.</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl">
      <SexualityPicker
        prefs={feedPrefs}
        onGender={setGender}
        onOrientation={setOrientation}
        step={1}
      />

      <div className="mb-2 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[3px] text-pow">
        <span className="inline-block h-px w-6 bg-pow/50" /> 2. NSFW TAGS — PICK YOUR POISON{' '}
        <span className="inline-block h-px w-6 bg-pow/50" />
      </div>
      <h2 className="goon-title mb-1 text-center text-4xl">CHALLENGE TAGS</h2>
      <p className="mb-5 text-center text-sm text-[#f3c6ff]">
        {prefsReady ? 'Now pick what hits the feed.' : 'Set sexuality above, then choose tags.'}
      </p>

      <div
        className={clsx(
          'cyber-panel cyber-pink p-5 transition',
          !prefsReady && 'pointer-events-none opacity-50',
        )}
      >
        <div className="flex flex-wrap justify-center gap-2.5">
          {tags.map((t) => {
            const on = selected.has(t.id)
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                disabled={!prefsReady}
                className={clsx(
                  'relative rounded-lg border-2 px-5 py-2 font-comic text-lg uppercase tracking-wide transition active:scale-[0.97]',
                  on
                    ? 'border-pow bg-pow/20 text-white shadow-neon-pink'
                    : 'border-edge bg-[#0a0a10] text-[#e7e7f2] hover:border-pow/60 hover:text-pow',
                )}
                aria-pressed={on}
              >
                <span className="absolute -left-1 -top-1 rounded bg-pow px-1 text-[8px] font-bold text-white">18+</span>
                {t.label}
              </button>
            )
          })}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-edge pt-4">
          <button
            type="button"
            disabled={!prefsReady}
            className="text-xs font-bold uppercase tracking-widest text-muted underline transition hover:text-pow disabled:no-underline disabled:opacity-40"
            onClick={() =>
              setSelected((s) => (s.size === tags.length ? new Set() : new Set(tags.map((t) => t.id))))
            }
          >
            {selected.size === tags.length ? 'Clear all' : 'Select all NSFW'}
          </button>

          {showKind ? (
            <div className="flex overflow-hidden rounded-lg border border-edge">
              {KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  disabled={!prefsReady}
                  className={clsx(
                    'px-3 py-1.5 font-comic text-sm uppercase transition',
                    kind === k.value
                      ? 'bg-kapow text-ink shadow-neon-cyan'
                      : 'bg-surface2 text-muted hover:text-white',
                  )}
                >
                  {k.label}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-xs uppercase tracking-widest text-muted">Images only</span>
          )}
        </div>
      </div>

      {showKind && kind === 'both' ? (
        <p className="mt-2 text-center text-xs text-muted">
          Images + video preloads while Bluesky loads.
        </p>
      ) : null}

      <div className="mt-6 text-center">
        <button
          type="button"
          disabled={!canStart}
          onClick={() => onStart([...selected], kind)}
          className="btn-goon px-12 py-3 text-2xl disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? 'LOADING…' : ctaLabel}
        </button>
        {!prefsReady ? (
          <p className="mt-2 text-xs text-muted">Set sexuality above to unlock tags and start</p>
        ) : selected.size === 0 ? (
          <p className="mt-2 text-xs text-muted">Pick at least one tag</p>
        ) : null}
      </div>
    </div>
  )
}